import type {
  ILauncherService,
  IModuleService,
  LaunchItem,
  ResolvedView,
  WorkbenchNavigationChangedPayload,
} from '@hibro/shared';
import {
  builtinLaunchKey,
  externalProcessKey,
  PLATFORM_MODULE_ID,
} from '@hibro/shared';
import {
  cn,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Icon,
  RailExpandedTitle,
  toast,
} from '@hibro/ui-client';
import { type ReactNode, useMemo, useRef } from 'react';

import { AppLinkIcon } from '../../app-links/icon';
import type { OpenExternalBridge } from '../../app-links/open';
import { useTranslator } from '../../i18n/i18n';
import {
  createLaunchActivator,
  resolveLaunchItemLabel,
} from '../../launcher/launch-activation';
import type { UiRegistry } from '../../runtime/ui-registry';
import { isDetachedInstance } from '../../surfaces/detached-views';
import type { ExternalBridge } from '../../surfaces/external-surface';
import type { IframeBridge } from '../../surfaces/iframe-surface';
import { SurfaceView } from '../../surfaces/surface-host';
import { useUserActivation } from '../../surfaces/user-activation-context';
import { parseInstanceId } from '../editor';
import { ActivityBarBadge } from './activity-bar-badge';
import { markActivityBarOpen } from './activity-bar-open';
import { resolveRailIconSpec } from './rail-icon';
import { RailFetchingIcon, RailItemSkeleton } from './rail-skeleton';
import { useRailExpanded } from './use-rail-expanded';

export interface RailItems {
  top: LaunchItem[];
  middle: LaunchItem[];
  bottom: LaunchItem[];
}

export interface ActivitySlotProps {
  railItems: RailItems;
  /** All contributed views (used to resolve view targets for rail items). */
  views: ResolvedView[];
  registry: UiRegistry;
  moduleService: IModuleService;
  launcherService: ILauncherService;
  /** Sync open for the browser runtime (见 21 / 18); keeps the user gesture. */
  openExternalBridge?: OpenExternalBridge;
  iframeBridge?: IframeBridge;
  /**
   * Bridge to the external-process host (§3.1/§5.8). Threaded into `custom` rail
   * views so a module's own launcher component can drive its processes through
   * `ViewProps.external` (e.g. draw its own run-state dot / start on click).
   */
  externalBridge?: ExternalBridge;
  /** Subscribe to contribution-point changes (见 17); backs `ViewProps.contributions`. */
  subscribeContributionsChanged?: (listener: () => void) => () => void;
  /**
   * Serialized handles of migrated surfaces (§5.8). Rail entries address whole,
   * singleton views, so they look themselves up with a key-less handle; the
   * editor area is where instance-keyed entries come from.
   */
  detachedSurfaces?: ReadonlySet<string>;
  /**
   * Process keys of the running external processes (架构方案 §3.1/§5.8):
   * `<moduleId>` for a single-entry module, `<moduleId>#<entryId>` for one of
   * several, so a module's two processes light their own launchers independently.
   */
  runningExternalProcesses?: ReadonlySet<string>;
  /** Module ids whose lazy `dist/` is currently downloading/unpacking (见 12
   * 两阶段下载); the matching rail icon pulses in place. */
  distFetchingModules?: ReadonlySet<string>;
  activeViewId: string | null;
  onDispatch: (command: string) => void;
  onOpenView: (viewId: string) => void;
  onFocusDetached?: (viewId: string) => void;
  /** Focus a running external process's native window, by its process key. */
  onActivateExternal?: (processKey: string) => void;
  onDetachView?: (viewId: string, moduleId: string) => void;
  onAttachView?: (viewId: string) => void;
  /** Report workbench navigation (see `platform.workbench.navigationChanged`). */
  onReportNavigation?: (payload: WorkbenchNavigationChangedPayload) => void;
}

const SETTINGS_LAUNCH_KEY = builtinLaunchKey('settings');

/**
 * Figma 5154:167985 lower cluster: the builtin settings entry stays above a
 * hairline; every other bottom item stays below it, in its existing order.
 * No hairline when nothing sits below settings.
 */
export function splitBottomCluster(items: readonly LaunchItem[]): {
  settings: LaunchItem | undefined;
  rest: LaunchItem[];
} {
  return {
    settings: items.find((item) => item.key === SETTINGS_LAUNCH_KEY),
    rest: items.filter((item) => item.key !== SETTINGS_LAUNCH_KEY),
  };
}

function BottomCluster({
  items,
  renderItem,
}: {
  expanded: boolean;
  items: LaunchItem[];
  renderItem: (item: LaunchItem) => ReactNode;
}): ReactNode {
  const { settings, rest } = splitBottomCluster(items);
  const belowSettings = rest.length > 0;
  return (
    <div className={cn('flex w-full flex-col gap-(--app-padding-m)')}>
      {settings ? renderItem(settings) : null}
      {settings && belowSettings ? (
        <div
          role="separator"
          className={cn(
            // Same mx-[9px] inset as the 36px icon rows.
            'mx-[9px] h-px shrink-0 bg-(--app-color-border)',
          )}
        />
      ) : null}
      {rest.map(renderItem)}
    </div>
  );
}

/**
 * ActivityBar launcher rail slot (架构方案 §5.7 / 见 21). Laid out as a fixed
 * top/middle/bottom structure driven by the merged {@link LaunchItem} list —
 * module contributions, built-in entries, and app links, after rail-layout
 * merge. A `customViewId` item renders the module's own component; otherwise an
 * icon button dispatches the item's `action`. Title shows once live width
 * leaves the 60px stop, and hides again once a shrinking rail falls below
 * 110px; the collapsed stop stays icon-only. Builtin settings is lifted above
 * the bottom hairline (Figma 5154:167985); the other bottom items keep their
 * order. That settings gear opens the preferences window.
 */
export function ActivitySlot({
  railItems,
  views,
  registry,
  moduleService,
  launcherService,
  openExternalBridge,
  iframeBridge,
  externalBridge,
  subscribeContributionsChanged,
  detachedSurfaces,
  runningExternalProcesses,
  distFetchingModules,
  activeViewId,
  onDispatch,
  onOpenView,
  onFocusDetached,
  onActivateExternal,
  onDetachView,
  onAttachView,
  onReportNavigation,
}: ActivitySlotProps): ReactNode {
  const { t, title } = useTranslator();
  const userActivation = useUserActivation();
  const rootRef = useRef<HTMLDivElement>(null);
  const expanded = useRailExpanded(rootRef);

  const resolveLabel = (item: LaunchItem): string =>
    resolveLaunchItemLabel(item, { t });

  // The rail and global search open a launchable through the same function
  // (见 21 打开漏斗), so neither can drift on the detached / resolveInstance /
  // external-focus / openUrl-toast branches.
  const activate = useMemo(
    () =>
      createLaunchActivator({
        views,
        detachedSurfaces,
        runningExternalProcesses,
        moduleService,
        launcherService,
        openExternalBridge,
        userActivation,
        onDispatch,
        onOpenView,
        onFocusDetached,
        onActivateExternal,
        onReportNavigation,
        translator: { t, title },
      }),
    [
      views,
      detachedSurfaces,
      runningExternalProcesses,
      moduleService,
      launcherService,
      openExternalBridge,
      userActivation,
      onDispatch,
      onOpenView,
      onFocusDetached,
      onActivateExternal,
      onReportNavigation,
      t,
      title,
    ],
  );

  const renderRailItem = (item: LaunchItem): ReactNode => {
    if (item.customViewId) {
      const view = views.find(
        (candidate) => candidate.id === item.customViewId,
      );
      if (!view) {
        return null;
      }
      const label = resolveLabel(item);
      const showExpandedTitle = item.showExpandedTitle !== false;
      const displaysExpandedTitle = expanded && showExpandedTitle;
      return (
        <div
          key={item.key}
          title={displaysExpandedTitle ? undefined : label}
          className={cn('relative flex w-full min-w-0 flex-none justify-start')}
          onPointerDown={() => {
            onReportNavigation?.({
              source: 'activityCustom',
              moduleId: item.moduleId,
              viewId: view.id,
            });
          }}
        >
          <SurfaceView
            viewId={view.id}
            moduleId={view.moduleId}
            registry={registry}
            moduleService={moduleService}
            kind={view.kind}
            url={view.url}
            iframeBridge={iframeBridge}
            externalBridge={externalBridge}
            subscribeContributionsChanged={subscribeContributionsChanged}
            onOpenView={onOpenView}
            hostPresentation={{
              kind: 'activityBar',
              expanded,
              title: label,
              showExpandedTitle,
            }}
            loadingFallback={
              <RailItemSkeleton
                expanded={expanded}
                showExpandedTitle={showExpandedTitle}
                label={label}
                loadingLabel={t('workbench.indicator.loading')}
              />
            }
          />
          <ActivityBarBadge
            badge={item.badge}
            className={
              expanded
                ? 'top-0 left-9 -translate-x-1/2 -translate-y-1/2'
                : 'top-0 left-[calc(50%+10px)] -translate-x-1/2 -translate-y-1/2'
            }
          />
        </div>
      );
    }

    const label = resolveLabel(item);
    const action = item.action;
    const targetView =
      action?.type === 'openView'
        ? views.find((view) => view.id === action.viewId)
        : undefined;
    const isDetached =
      action?.type === 'openView' &&
      isDetachedInstance(detachedSurfaces, action.viewId);
    const isExternal = targetView?.kind === 'external';
    const targetProcessKey =
      isExternal && targetView
        ? externalProcessKey(targetView.moduleId, targetView.externalEntry)
        : undefined;
    const isRunning =
      targetProcessKey !== undefined &&
      (runningExternalProcesses?.has(targetProcessKey) ?? false);
    const isFetchingDist =
      item.moduleId !== PLATFORM_MODULE_ID &&
      (distFetchingModules?.has(item.moduleId) ?? false);
    const isActive =
      action?.type === 'openView' &&
      activeViewId !== null &&
      parseInstanceId(activeViewId).viewId === action.viewId;
    const interactive = action !== undefined;
    // 见 21：拥有者为「该项被再次激活」注册的回调，只在它的视图挂载时存在——所以「拿不到」
    // 正好等于「没有界面可响应」，包括视图被 detach 出去的情形（那时 isActive 本就为 false）。
    // tooltip 可以接受晚一帧（缺提示，不误导）；真正派发必须在点击当下查，因为默认启动的
    // 活动项先画侧栏、视图 useEffect 才写入 Map，那一次写入不会让侧栏重渲。
    const reactivateHintReady = isActive
      ? registry.getRailActivation(item.key)
      : undefined;

    // 见 21：声明的 action 照旧跑，再激活回调只是追加在后面。刻意不是「有回调就取而代之」——
    // 那等于让 `action` 在某些条件下不生效，而 rail 项的点击语义是平台最基础的契约之一。
    const handleClick = () => {
      // Already-active clicks stay on the re-activation handler. A click that
      // switches to this view bumps a generation the view reads when it becomes
      // visible, so that show is not confused with a tab drag.
      if (!isActive && action?.type === 'openView') {
        markActivityBarOpen(action.viewId);
      }
      activate(item, 'activityBar');
      if (isActive) {
        registry.getRailActivation(item.key)?.();
      }
    };

    const canDetach =
      action?.type === 'openView' &&
      targetView?.detachable === true &&
      onDetachView !== undefined;
    const canPinToggle = !item.locked;
    const showExpandedTitle = expanded && item.showExpandedTitle !== false;

    const railIcon = resolveRailIconSpec(item, isActive);
    // A full-color resource icon is a face that draws its own disc (Figma
    // 5154:167985 `verified server`, 36): it fills the 36 slot unpadded.
    // Mono resources and builtins are glyphs and stay in the 20 slot.
    const isFaceIcon =
      item.source !== 'app-link' &&
      railIcon?.type === 'resource' &&
      railIcon.mono !== true;

    const tooltip = isDetached
      ? t('workbench.view.detachedFocusTitle', { title: label })
      : isRunning
        ? t('workbench.view.runningFocusTitle', { title: label })
        : label;
    // 见 21：再激活手势没有任何视觉暗示，所以把拥有者给的提示挂进 tooltip——但只在**手势此刻
    // 真会生效**时（该项已激活且回调在册），提示才不会承诺一件不会发生的事。
    const reactivateHint =
      reactivateHintReady && item.reactivateHint
        ? title(item.moduleId, item.reactivateHint)
        : undefined;
    const hintedTooltip = reactivateHint
      ? `${tooltip}\n${reactivateHint}`
      : tooltip;

    const menuItem = (
      <div
        // 展开态本来不给 tooltip（标题已画在图标旁），但提示没有别的落脚处，故单独留给它。
        title={showExpandedTitle ? reactivateHint : hintedTooltip}
        onClick={interactive ? handleClick : undefined}
        className={cn(
          // Fixed 9px inset on both stops, so the icon stays put while the
          // rail is dragged. Expanded adds the title gap; no justify-center.
          // The middle lane reserves the scrollbar gutter and gives it back
          // on each row (see `.app-rail-scroll` in dockview-theme.css).
          'mx-[9px] flex h-9 min-w-0 flex-1 items-center rounded-xl font-normal',
          expanded && 'gap-2.5',
          isActive
            ? 'bg-(--app-color-accent-subtle)'
            : isDetached
              ? 'text-(--app-color-text-disabled)'
              : 'text-(--app-color-text-secondary)',
          interactive ? 'cursor-pointer' : 'cursor-not-allowed',
        )}
      >
        <div
          // 36 slot stays on the centerline. The badge anchors to the glyph
          // (20) or the face (36) inside, not this slot.
          className="flex size-9 shrink-0 items-center justify-center"
        >
          <RailFetchingIcon
            fetching={isFetchingDist}
            face={isFaceIcon}
            className={isActive ? 'text-(--app-color-accent)' : undefined}
          >
            {item.source === 'app-link' || item.action?.type === 'openUrl' ? (
              <AppLinkIcon spec={item.icon} size={20} />
            ) : railIcon ? (
              <Icon
                spec={railIcon}
                fallbackText={label}
                size={isFaceIcon ? 36 : 20}
              />
            ) : (
              label.charAt(0).toUpperCase()
            )}
            {isRunning ? (
              <span
                title={t('workbench.indicator.running')}
                className="absolute top-0 right-0 size-2 translate-x-1/2 -translate-y-1/2 rounded-full bg-success"
              />
            ) : null}
            <ActivityBarBadge
              badge={item.badge}
              className="top-0 right-0 translate-x-1/2 -translate-y-1/2"
            />
          </RailFetchingIcon>
        </div>
        <RailExpandedTitle
          shown={showExpandedTitle}
          className={cn(isActive && 'text-(--app-color-text-primary)')}
        >
          {label}
        </RailExpandedTitle>
      </div>
    );

    const itemShellClass = 'flex w-full min-w-0 flex-none justify-start';

    if (!canDetach && !canPinToggle) {
      return (
        <div key={item.key} className={itemShellClass}>
          {menuItem}
        </div>
      );
    }

    return (
      <ContextMenu key={item.key}>
        <ContextMenuTrigger render={<div className={itemShellClass} />}>
          {menuItem}
        </ContextMenuTrigger>
        <ContextMenuContent>
          {canPinToggle ? (
            <ContextMenuItem
              onClick={() => {
                void launcherService.setPinned(item.key, false).catch(() => {
                  toast(t('settings.rail.locked'));
                });
              }}
            >
              {t('settings.rail.unpin')}
            </ContextMenuItem>
          ) : null}
          {canDetach && action?.type === 'openView' ? (
            <>
              {canPinToggle ? <ContextMenuSeparator /> : null}
              {isDetached ? (
                <>
                  {onFocusDetached ? (
                    <ContextMenuItem
                      onClick={() => onFocusDetached(action.viewId)}
                    >
                      {t('workbench.editor.focusWindow')}
                    </ContextMenuItem>
                  ) : null}
                  {onAttachView ? (
                    <>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        onClick={() => onAttachView(action.viewId)}
                      >
                        {t('workbench.editor.attachBack')}
                      </ContextMenuItem>
                    </>
                  ) : null}
                </>
              ) : (
                <ContextMenuItem
                  onClick={() =>
                    targetView &&
                    onDetachView?.(action.viewId, targetView.moduleId)
                  }
                >
                  {t('surface.detach.title')}
                </ContextMenuItem>
              )}
            </>
          ) : null}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        // Figma 200:38562. padding/12 → --app-padding-s (12), avatar-to-tabs
        // padding/20 → --app-padding-2xl (20), tab rows padding/14 →
        // --app-padding-m (14). Bottom padding stays pb-7 (5154:167984).
        'flex h-full w-full flex-col pt-(--app-padding-s) pb-7',
      )}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-(--app-padding-2xl)">
        <div className="flex w-full flex-col">
          {railItems.top.map(renderRailItem)}
        </div>
        <div className="app-rail-scroll flex min-h-0 w-full flex-auto flex-col gap-(--app-padding-m) overflow-y-auto overflow-x-hidden">
          {railItems.middle.map(renderRailItem)}
        </div>
      </div>
      <BottomCluster
        expanded={expanded}
        items={railItems.bottom}
        renderItem={renderRailItem}
      />
    </div>
  );
}
